import { useTracking } from "core/tracking/useTracking";
import { ForceCreateModalType, useAppValues } from "lib/context/AppContext";
import { PrimaryColorPalette, TextAndShapesPalette } from "lib/styles/colors";
import { parseCookies } from "nookies";
import { ReactElement, useState } from "react";
import styled from "styled-components";
import { formatDateForDisplay } from "../helpers";
import { ActivityAction, NewsFeedArticle } from "../types";
import { track } from "lib/analytics/analytics";
import * as gtag from "lib/analytics/gtag";
import {
  AnalyticsEvents,
  InternalAnalyticsEvents,
} from "lib/analytics/constants";
import { handleNewsActivity } from "../service";
import ArticleIcon from "lib/svgs/ArticleIcon";
import Text, { TextType } from "components/Text";
import HeartSmallIcon from "lib/svgs/HeartSmallIcon";
import { ResponseStatus } from "lib/http/types";

const Container = styled.a`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  max-width: 820px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${PrimaryColorPalette.MAIN_6};
  border: 1px solid ${TextAndShapesPalette.BRAND_BLACK};
  cursor: pointer;
`;

const ImageContainer = styled.div`
  width: 120px;
  height: 120px;
  min-width: 120px;
  min-height: 120px;
  border-radius: 8px;
  overflow: hidden;
`;

const StyledImage = styled.img`
  width: 120px;
  height: 120px;
`;

const RightSideVStack = styled.div`
  display: flex;
  margin-left: 24px;
  flex-direction: column;
  justify-content: flex-start;
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const IconContainer = styled.div`
  margin-bottom: 1px;
`;

interface Props {
  article: NewsFeedArticle;
  userRefId?: string | null;
}

export function NewsFeedCard({ article }: Props): ReactElement {
  const { internalTrackEvent } = useTracking();

  const { userRefId } = parseCookies();
  const {
    image_url,
    source,
    published_at,
    title,
    link,
    num_likes,
    usr_liked,
    ref_id,
  } = article;
  const formattedTime = formatDateForDisplay(published_at);
  const [currentlyLiked, setCurrentlyLiked] = useState<boolean>(
    usr_liked ?? false
  );
  const [localLikes, setLocalLikes] = useState<number>(num_likes ?? 0);
  const { setAppValues } = useAppValues();

  /**
   * If userRefId , handle Likes.. otherwise show signup modal
   */
  const handleToggleLike = async () => {
    if (!userRefId) {
      setAppValues({ forceCreateAccountModal: ForceCreateModalType.NEWS });
      return;
    }

    setLocalLikes(currentlyLiked ? localLikes - 1 : localLikes + 1);
    setCurrentlyLiked(!currentlyLiked);
    const result = await handleNewsActivity({
      activity: usr_liked ? ActivityAction.UNLIKED : ActivityAction.LIKED,
      refId: ref_id,
    });
    if (result.type === ResponseStatus.ERROR) {
      setCurrentlyLiked(!currentlyLiked);
      setLocalLikes(num_likes ?? 0);
    }
    // const output = await mutateFeedArticle({
    //   feed: feed ?? [], refId: ref_id, liked: !usr_liked })
    // setFeedValues({
    //   feed: output,
    //   hasMutated: true
    // })

    internalTrackEvent(InternalAnalyticsEvents.NEWS_ARTICLE_LIKE_CLICKED);
  };

  const handleTrack = () => {
    gtag.event({
      action: "news_click_article",
      category: "engagement",
    });
    internalTrackEvent(InternalAnalyticsEvents.NEWS_ARTICLE_VIEWED);
    track(AnalyticsEvents.CLICKED_NEWS_ARTICLE);
  };

  return (
    // <a href={link} target={"_blank"} rel='noopener noreferrer'>
    <Container
      href={link}
      target={"_blank"}
      rel="noopener noreferrer"
      onClick={handleTrack}
    >
      <ImageContainer>
        <StyledImage src={String(image_url)} alt="News article preview" />
      </ImageContainer>
      <RightSideVStack>
        <ContentRow>
          <IconContainer>
            <ArticleIcon />
          </IconContainer>
          <Text type={TextType.PARAGRAPH} style={{ marginLeft: "8px" }}>
            {source}
          </Text>
          {/* Dot icon */}
          <Text
            type={TextType.PARAGRAPH}
            style={{ marginLeft: "8px", marginRight: "8px" }}
          >
            &#183;
          </Text>
          <Text type={TextType.PARAGRAPH}>{formattedTime}</Text>
        </ContentRow>
        <Text
          type={TextType.TITLE_1}
          style={{ marginBottom: "8px", marginTop: "8px" }}
        >
          {title}
        </Text>
        <ContentRow onClick={handleToggleLike}>
          <IconContainer>
            <HeartSmallIcon enabled={currentlyLiked} />
          </IconContainer>
          <Text type={TextType.PARAGRAPH} style={{ marginLeft: "4px" }}>
            {`${localLikes} ${localLikes === 1 ? "Like" : "Likes"}`}
          </Text>
        </ContentRow>
      </RightSideVStack>
    </Container>
    // </a>
  );
}
